import { Show, type Component } from 'solid-js';

import { iconArtUrl } from './iconArt';

/**
 * An item's or a skill's picture, falling back to its glyph.
 *
 * ── Why this is a component and not two lines at each call site ──────────────
 *
 * There are seven places that draw an item or a skill: the bag grid, the paper doll,
 * the vault list, the vendor's offers, the drag ghost, an action-bar slot and the skills
 * panel. Every one of them had `<span class="…__glyph">{item.glyph}</span>` written out,
 * and the fallback rule — *draw the art when this build has it, the glyph when it does
 * not* — is a rule that has to hold at all seven or the inventory is half illustrated.
 *
 * A rule spelled seven times is a rule that will be spelled six times after the next
 * panel is added. This is the same reason `affixLine` is the only affix renderer: the
 * fault that motivated it was a fourth copy whose fallback printed a raw stat key, and
 * nothing failed — it just looked slightly wrong to the player and to nobody else.
 *
 * ── Why the glyph survives ──────────────────────────────────────────────────
 *
 * 234 catalogue entries name an icon and the art arrives one generated sheet at a time,
 * so most ids have no file yet and some may never get one. The glyph is not a
 * placeholder to be deleted when the last sheet lands: it is what a client renders for
 * an id a newer server sent it, and `†` is legible where a broken image is not.
 */
export const Icon: Component<{
  /** The catalogue's `icon_id`. Absent, or unknown to this build, draws the glyph. */
  iconId?: string;
  /** The character to draw when there is no art. */
  glyph: string;
  /** The call site's own class — it owns the size, so `.vault__glyph` stays small. */
  class: string;
  /** For the art only; a glyph is decorative either way. */
  alt?: string;
}> = (props) => {
  const url = () => iconArtUrl(props.iconId);
  return (
    // `data-icon` rather than a modifier class so the CSS that gives an icon the whole
    // socket can key off *having art*, which is the thing that changes the layout —
    // a call site cannot forget to add it.
    <span class={props.class} data-icon={url() ? 'art' : 'glyph'} aria-hidden={!props.alt}>
      <Show when={url()} fallback={props.glyph}>
        {(src) => (
          // `draggable={false}`: an image is natively draggable and would start the
          // browser's own drag, which cancels the pointer sequence the inventory's drag
          // and drop is built on. Every icon sits inside a drag source.
          <img class="item-icon" src={src()} alt={props.alt ?? ''} draggable={false} />
        )}
      </Show>
    </span>
  );
};
